import { useEffect, useState } from 'react'
import {
  getMerchantLookupWithToken,
  getPaymentAdapterLookup,
  getPaymentMethodLookup,
} from '../../../common/core/_requests'

export interface ISelectOption {
  value: string
  label: string
}

/**
 * Опции фильтров отчёта грузятся облегчёнными lookup-ручками, а не постраничными списками
 * справочников (CPAYMENT-1760).
 *
 * Три разных контракта value, поэтому маппинг разный:
 * - мерчанты — `lookup-with-token`, value уже токен, он и уходит в `merchantTokenList`;
 * - адаптеры — value это id, в запросе он нужен числом;
 * - методы — value это id, а бэкенд фильтрует по ТИПУ метода, поэтому в значение кладём `label`.
 *
 * Роли у ручек разные: `lookup-with-token` доступен всем ролям отчёта, а адаптеры — только
 * менеджеру и менеджеру мерчантов, поэтому их фильтр прячется через `visibility`, а не молча
 * показывает пустой список.
 */
export const useMatchingPayoutFilterOptions = (canLoadAdapters: boolean) => {
  const [merchantOptions, setMerchantOptions] = useState<ISelectOption[]>([])
  const [adapterOptions, setAdapterOptions] = useState<ISelectOption[]>([])
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<ISelectOption[]>([])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const [merchants, methods] = await Promise.all([
          getMerchantLookupWithToken(),
          getPaymentMethodLookup(),
        ])

        if (cancelled) {
          return
        }

        if (merchants.data?.success && merchants.data.data) {
          setMerchantOptions(
            merchants.data.data.map((item) => ({ value: String(item.value), label: item.label }))
          )
        }

        if (methods.data?.success && methods.data.data) {
          // Значение = тип метода: в теле запроса бэкенд ждёт `paymentMethods: ['SBP P2P RUB']`.
          setPaymentMethodOptions(
            methods.data.data.map((item) => ({ value: item.label, label: item.label }))
          )
        }
      } catch {
        // Пустой справочник = фильтр без опций; сам отчёт при этом остаётся рабочим.
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!canLoadAdapters) {
      return
    }

    let cancelled = false

    const loadAdapters = async () => {
      try {
        const { data } = await getPaymentAdapterLookup({ enabledOnly: true })

        if (!cancelled && data?.success && data.data) {
          setAdapterOptions(
            data.data.map((item) => ({ value: String(item.value), label: item.label }))
          )
        }
      } catch {
        // см. выше
      }
    }

    loadAdapters()

    return () => {
      cancelled = true
    }
  }, [canLoadAdapters])

  return { merchantOptions, adapterOptions, paymentMethodOptions }
}
